import React, { useMemo } from 'react';

import { EnvelopeEndConnectionType, Point2D, TrussEnvelope } from '@paragon/contract-library';
import { minimaFuzzy } from '@paragon/geometry-library';
import {
  calculateTrussPolygon2D,
  getEnvelopeSegmentFromEnvelope,
  TrussSolidCalculationDependencies,
} from '@paragon/layout';

import { useAppSelector } from '../../../components/hooks';
import { getTrussSolidCalculationDependencies } from '../../../library/layout-geometry/truss-envelopes/trussSolidSelectors';
import { RenderModeString } from '../../../library/renderModes';
import { is3DLayoutCameraInUse } from '../../../store/selectors/layoutModeSelectors';
import { getActiveConnectionMode, getActiveEnvelopeMode } from '../../../store/selectors/layoutVisualOptionsSelectors';
import { EnvelopeRenderMode } from '../../../store/slices/visualOptionsSlice';
import { LabelVisual } from '../../util/LabelVisual';
import { layoutColors } from '../layoutColors';

export function TrussEnvelopeCarriedConnectionVisualsController(props: { envelopes: TrussEnvelope[] }) {
  const using3DCamera = useAppSelector(is3DLayoutCameraInUse);

  const calculationDependencies = useAppSelector(getTrussSolidCalculationDependencies);

  const renderMode = useAppSelector(getActiveConnectionMode);
  const envelopeRenderMode = useAppSelector(getActiveEnvelopeMode);

  const shouldRender = renderMode === 'connections' || renderMode === 'hardwareAndConnections';

  return shouldRender
    ? props.envelopes.map((envelope) => (
        <TrussEnvelopeCarriedConnectionVisuals
          key={envelope.guid}
          envelope={envelope}
          in3D={using3DCamera}
          envelopeRenderMode={envelopeRenderMode}
          calculationDependencies={calculationDependencies}
        />
      ))
    : null;
}

interface Props {
  envelope: TrussEnvelope;

  in3D: boolean;
  envelopeRenderMode: RenderModeString<EnvelopeRenderMode>;
  calculationDependencies: TrussSolidCalculationDependencies;
}

export const TrussEnvelopeCarriedConnectionVisuals = ({
  envelope,
  in3D,

  envelopeRenderMode,
  calculationDependencies,
}: Props) => {
  const bumpElevation =
    !in3D ||
    envelopeRenderMode === 'trussSolids' ||
    (envelopeRenderMode === 'components' && envelope.componentDesignGuid != null);

  const elevations = useMemo(() => {
    if (envelope.leftEndConnectionOverride == null && envelope.rightEndConnectionOverride == null) return null;

    if (!bumpElevation) return { basePointSideElevation: 0, endPointSideElevation: 0 };

    const previewPolygon = calculateTrussPolygon2D(envelope, calculationDependencies);
    if (!previewPolygon) return { basePointSideElevation: 0, endPointSideElevation: 0 };

    const basePointSideElevation = Math.max(...minimaFuzzy(previewPolygon.vertices, ({ x }) => x).map(({ y }) => y));
    const endPointSideElevation = Math.max(...minimaFuzzy(previewPolygon.vertices, ({ x }) => -x).map(({ y }) => y));

    return { basePointSideElevation, endPointSideElevation, topSide: true };
  }, [calculationDependencies, envelope, bumpElevation]);

  const centerSegment = getEnvelopeSegmentFromEnvelope(envelope, 'Center');

  if (!centerSegment || !elevations) return null;

  const thickness = envelope.thickness * envelope.numberOfPlies;
  return (
    <>
      {envelope.leftEndConnectionOverride && (
        <TrussEnvelopeCarriedConnectionVisual
          location={centerSegment.basePoint}
          thickness={thickness}
          elevation={envelope.elevation + elevations.basePointSideElevation}
          topSide={!!elevations.topSide}
          connection={envelope.leftEndConnectionOverride}
        />
      )}

      {envelope.rightEndConnectionOverride && (
        <TrussEnvelopeCarriedConnectionVisual
          location={centerSegment.endPoint}
          thickness={thickness}
          elevation={envelope.elevation + elevations.endPointSideElevation}
          topSide={!!elevations.topSide}
          connection={envelope.rightEndConnectionOverride}
        />
      )}
    </>
  );
};

const TrussEnvelopeCarriedConnectionVisual = ({
  location,
  thickness,
  elevation,
  topSide,
  connection,
}: {
  location: Point2D;
  thickness: number;
  elevation: number;
  topSide: boolean;
  connection: EnvelopeEndConnectionType;
}) => {
  const color = layoutColors.preferredConnections[connection];

  if (connection === 'None') {
    const topSideOffset = topSide ? 1 : 0;
    return (
      <LabelVisual
        position={[location.x, elevation + topSideOffset, -location.y]}
        fontSize={2}
        color={color}
        alwaysOnTop={false}
      >
        X
      </LabelVisual>
    );
  }

  return (
    <mesh position={[location.x, elevation, -location.y]}>
      <meshBasicMaterial color={color} />
      <sphereGeometry args={[thickness / 2]} />
    </mesh>
  );
};
